import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import {
  Button,
  FormControl,
  MenuItem,
  Grid,
  CircularProgress,
  Box,
  TextField,
} from "@mui/material";
import Layout from "components/structure/Layout";
import { useLocation, useNavigate } from "react-router-dom";
import { SnackbarContext } from "context/snackBarContext";
import DataTable from "utils/DataTable/DataTable";
import NoData from "assets/noData.svg";
import CommonService from "services/CommonService";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import CropSeasonListService from "services/CropSeasonListService";

const RegistrationOptions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Heading = styled.div`
  color: rgba(115, 125, 134, 1);
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;
const DataContainer = styled.div``;

const CropSeasonList = () => {
  dayjs.extend(utc);

  const location = useLocation();
  const { state } = location;
  const [cropYear, setCropYear] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = React.useState(false);
  const [cropYears, setCropYears] = useState([]);
  const [countries, setCountries] = useState([]);
  const [firstCountry, setFirstCountry] = useState(null);
  const [selectCountry, setSelectCountry] = useState("");

  const user = JSON.parse(sessionStorage.getItem('BAT_USER'));
  const navigate = useNavigate();
  const {
    severity,
    message,
    openAlert,
    setOpenAlert,
    setSeverity,
    setMessage,
  } = React.useContext(SnackbarContext);

  const columns = [
    {
      field: "countryName",
      headerName: "Country",
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "cyCropYear",
      headerName: "Crop Year",
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "seasonCode",
      headerName: "Season Code",
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "StartFormatDate",
      headerName: "Start Date",
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "EndFormatDate",
      headerName: "End Date",
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
  ];

  // Initialize countries on component mount
  useEffect(() => {
    CommonService.fetchCountries()
      .then((response) => {
        const data = response.data.response;
        setCountries(data);
        if (data.length > 0) {
          setFirstCountry(data[0]);
          if (user?.profileAttribute === "Global" && user?.countryCode) {
            //setSelectCountry(user.countryCode);
          } else {
            //setSelectCountry(data[0]?.countryCode);
          }
        }
      })
      .catch((error) => console.error("Error fetching countries:", error));
  }, []);

  // Fetch crop years when country changes
  useEffect(() => {
    if (selectCountry) {
      const headers = {
        apiCountryCode: selectCountry,
      };
      CropSeasonListService.getCropYears({ headers })
        .then((response) => {
          const cropYearsFromApi = response.data.response.map(
            (item) => item.cropYear
          );
          setCropYears(cropYearsFromApi);
        })
        .catch((error) => {
          setCropYears([]);
          console.error("Error fetching crop years:", error);
        });
    }
  }, [selectCountry]);

  // Main function to fetch season registration data
  const fetchSeasonRegistration = () => {
    if (cropYear === "default") {
      setMessage("Please select crop year");
      setOpenAlert(true);
      setSeverity("warning");
      return;
    }

    setLoading(true);
    setRows([]);

    // Prepare headers with country code
    const headers = {
      apiCountryCode: selectCountry,
    };

    // Call API with cropYear as parameter and headers
    CropSeasonListService.getSeasonRegistration(cropYear,  {headers} )
      .then((res) => {
        setLoading(false);
        let data = res?.data?.response || res?.data || [];
        
        // If data is an array, use it directly; otherwise try to extract array from response
        if (!Array.isArray(data)) {
          data = data?.data || data?.seasonRegistrations || [];
        }

        // Format the data
        data?.forEach((ele, index) => {
          ele.id = index;
          if (ele.startDate) {
            ele.StartFormatDate = dayjs.utc(ele.startDate).format("DD/MM/YYYY");
          }
          if (ele.endDate) {
            ele.EndFormatDate = dayjs.utc(ele.endDate).format("DD/MM/YYYY");
          }
        });

        setRows(data);
      })
      .catch((error) => {
        setLoading(false);
        setMessage(error?.response?.data?.message || error?.message || "Error fetching data");
        setOpenAlert(true);
        setSeverity("error");
        console.error("Error fetching season registration:", error);
      });
  };



  // Handle crop year change
  const handleChangeYear = (event) => {
    setCropYear(event.target.value);
  };

  // Handle country change
  const handleChangeCountry = (event) => {
    setSelectCountry(event.target.value);
    setCropYear("default"); // Reset crop year when country changes
    setRows([]); // Clear existing data
  };

  // Handle Get button click
  const handleGetData = () => {
    fetchSeasonRegistration();
  };

  return (
    <Layout title="Crop Stage Calendar">
      <div className="CropStageCalendarContainer">
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          columns={{ xs: 4, sm: 8, md: 12 }}
        >
          <Grid item xs={8} sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ marginLeft: "1rem", marginTop: 2 }}>
              <Heading>Crop Season List</Heading>
            </Box>
          </Grid>
        </Grid>
        
        <div>
          <RegistrationOptions>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8} xl={8}>
                {/* Country Selection */}
                <FormControl sx={{ m: 1, minWidth: "200px" }}>
                  <TextField
                    select
                    value={selectCountry}
                    displayEmpty
                    onChange={handleChangeCountry}
                    inputProps={{ "aria-label": "Without label" }}
                    label="Select Country"
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            maxHeight: 250,
                          },
                        },
                      },
                    }}
                  >
                    {countries?.map((country) => (
                      <MenuItem
                        key={country.countryId}
                        value={country.countryCode}
                      >
                        {country.countryName}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>

                {/* Crop Year Selection */}
                <FormControl sx={{ m: 1, minWidth: "200px" }}>
                  <TextField
                    select
                    value={cropYear}
                    onChange={(e) => handleChangeYear(e)}
                    displayEmpty
                    inputProps={{ "aria-label": "Without label" }}
                    label="Crop Year"
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            maxHeight: 250,
                          },
                        },
                      },
                    }}
                  >
                    
                    {cropYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>

                {/* Get Button */}
                <Button
                  sx={{
                    borderRadius: 4,
                    minWidth: 100,
                    minHeight: 50,
                    background: "#102A62",
                    marginTop: "13px",
                    marginLeft: "10px",
                  }}
                  variant="contained"
                  onClick={() => handleGetData()}
                  disabled={loading || cropYear === "default" || !selectCountry}
                >
                  {loading && <CircularProgress size={20} />}
                  Get
                </Button>
              </Grid>
            </Grid>
          </RegistrationOptions>

          {/* Data Display */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {rows && rows.length > 0 ? (
                <DataContainer>
                  <DataTable
                    getRowId={(row) => row?.id || row?.seasonRegistrationId}
                    rows={rows}
                    columns={columns}
                  />
                </DataContainer>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column",
                    alignItems: "center",
                    mt: 4,
                  }}
                >
                  <img
                    src={NoData}
                    style={{ height: "100px" }}
                    alt="No Data"
                  />
                  <p>No Data Available</p>
                </Box>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CropSeasonList;